import { useEffect, useState } from "react";
import api from "@/lib/api";

let _cachedTerms = null;
let _cachedPromise = null;

/** Fetch (once) and cache the Kitchen Help term index. Returns
 * { termsByLower: Map<lowerTerm, {slug, term}>, sortedKeys: string[] }.
 * `sortedKeys` is sorted DESC by length so longer phrases match first
 * ("spring onion" wins over "onion"). */
export function useKitchenHelpIndex() {
  const [index, setIndex] = useState({ termsByLower: null, sortedKeys: [] });

  useEffect(() => {
    if (_cachedTerms) {
      setIndex(_cachedTerms);
      return;
    }
    if (!_cachedPromise) {
      _cachedPromise = api
        .get("/public/kitchen-help")
        .then((r) => {
          const map = new Map();
          for (const t of r.data.items || []) {
            const primaryKey = (t.term || "").toLowerCase().trim();
            if (primaryKey) map.set(primaryKey, { slug: t.slug, term: t.term });
            for (const aka of t.also_known_as || []) {
              // strip "(US)", "(UK)" etc for matching
              const clean = String(aka)
                .replace(/\([^)]*\)/g, "")
                .toLowerCase()
                .trim();
              if (clean && !map.has(clean)) map.set(clean, { slug: t.slug, term: t.term });
            }
          }
          const sortedKeys = Array.from(map.keys()).sort((a, b) => b.length - a.length);
          _cachedTerms = { termsByLower: map, sortedKeys };
          return _cachedTerms;
        })
        .catch(() => ({ termsByLower: new Map(), sortedKeys: [] }));
    }
    _cachedPromise.then((idx) => setIndex(idx));
  }, []);

  return index;
}

const IGNORE_WORDS = new Set([
  "salt", "pepper", "water", "oil", "sugar", "flour", "egg", "eggs",
  "onion", "onions", "garlic", "milk", "butter",
]); // super-common words we don't want cluttered with dotted links

/**
 * Render `text` with any Kitchen Help term names auto-wrapped as
 * subtle underlined anchor links to /kitchen-help#slug.
 *
 * Longest-first matching (so "spring onion" wins over "onion"), skips
 * the ultra-common `IGNORE_WORDS`, and only wraps the FIRST occurrence
 * per string to avoid visual noise on long ingredient lines.
 */
export function KitchenHelpLinkify({ text, index }) {
  if (!text || !index || !index.termsByLower || index.termsByLower.size === 0) {
    return <>{text}</>;
  }
  const lower = text.toLowerCase();
  let bestKey = null;
  let bestStart = -1;
  for (const key of index.sortedKeys) {
    if (IGNORE_WORDS.has(key)) continue;
    // Word-boundary regex — safer than plain indexOf for "rice" vs "spice"
    const re = new RegExp(`\\b${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    const m = lower.match(re);
    if (m && m.index !== undefined) {
      bestKey = key;
      bestStart = m.index;
      break; // longest match wins
    }
  }
  if (!bestKey || bestStart < 0) return <>{text}</>;
  const entry = index.termsByLower.get(bestKey);
  if (!entry) return <>{text}</>;
  const before = text.slice(0, bestStart);
  const matched = text.slice(bestStart, bestStart + bestKey.length);
  const after = text.slice(bestStart + bestKey.length);
  return (
    <>
      {before}
      <a
        href={`/kitchen-help/${entry.slug}`}
        className="underline decoration-dotted decoration-rf-amber underline-offset-2 hover:text-rf-orange"
        title={`Kitchen Help — ${entry.term}`}
        data-testid={`kh-link-${entry.slug}`}
      >
        {matched}
      </a>
      {after}
    </>
  );
}
